/**
 * @file 画面を覆う小さな対話 (教材の詳細・単元の詳細)。
 *
 * @what: 背景を暗くした上に枠を出し、Esc・閉じるボタン・余白のクリック (pointerdown も余白のときだけ)
 *   で閉じる。開いている間は本文のスクロールを止め、閉じるボタンに初期フォーカスを置く。
 * @why: 見取り図の表は広く、教材や単元の編集を表の中に置くと崩れる。タップで開く対話に寄せる
 *   (代表 2026-08-24)。FullscreenViewer と同じ overlay 方式 (iOS Safari でも動く)。
 * @failure: 描画のみ。
 */

import { useEffect, useRef, type ReactNode } from "react";
import "./study.css";

export function StudyDialog({
  title,
  onClose,
  children,
  labelledBy,
  size = "normal",
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** 見出しの id (aria-labelledby)。 */
  labelledBy: string;
  /** 横幅である。wide は手書きのように面積が必要なもの (画面の幅いっぱい近くまで広げる)。 */
  size?: "normal" | "wide";
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const downOnBackdrop = useRef(false);
  // 親が毎回新しい関数を渡しても、Esc の購読は1回だけにする。
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    // Esc は一番上の対話だけを閉じ、外 (全画面ビューワ) には届かせない。一番上は文書の並びで
    // 最後の対話とする。※なぜなら対話の上に対話を重ねたとき (AI 出力 → 問ごとの解説)、全部が
    // document の keydown を聞いていると1回の Esc で全部が閉じ、window まで届くとビューワも
    // 閉じてリーダーから追い出されるためである。
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const all = document.querySelectorAll(".study-dialog-backdrop");
      if (all[all.length - 1] !== backdropRef.current) return;
      e.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return (
    <div
      ref={backdropRef}
      className="study-dialog-backdrop"
      onPointerDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && downOnBackdrop.current) onClose();
        downOnBackdrop.current = false;
      }}
    >
      <div
        className={size === "wide" ? "study-dialog study-dialog--wide" : "study-dialog"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        <div className="study-dialog-head">
          <h3 id={labelledBy} className="study-dialog-title">
            {title}
          </h3>
          <button
            ref={closeRef}
            type="button"
            className="study-dialog-close"
            aria-label="閉じる"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="study-dialog-body">{children}</div>
      </div>
    </div>
  );
}
